import React, { createContext, useContext, useMemo, useReducer, useState } from 'react';

const CartContext = createContext(null);

const initialState = { items: [] };

function reducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const existing = state.items.find((i) => i.id === action.product.id);
      // Customised hamper replaces its previous line (new soap selection wins)
      if (existing && action.product.customNames) {
        return {
          items: state.items.map((i) =>
            i.id === action.product.id
              ? { ...i, ...action.product, quantity: 1 }
              : i
          ),
        };
      }
      if (existing) {
        return {
          items: state.items.map((i) =>
            i.id === action.product.id ? { ...i, quantity: i.quantity + 1 } : i
          ),
        };
      }
      return {
        items: [
          ...state.items,
          {
            id: action.product.id,
            name: action.product.name,
            price: action.product.price,
            image: action.product.image,
            customNames: action.product.customNames || null,
            quantity: 1,
          },
        ],
      };
    }
    case 'INC':
      return {
        items: state.items.map((i) =>
          i.id === action.id ? { ...i, quantity: i.quantity + 1 } : i
        ),
      };
    case 'DEC':
      return {
        items: state.items
          .map((i) => (i.id === action.id ? { ...i, quantity: i.quantity - 1 } : i))
          .filter((i) => i.quantity > 0),
      };
    case 'REMOVE':
      return { items: state.items.filter((i) => i.id !== action.id) };
    case 'CLEAR':
      return { items: [] };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [open, setOpen] = useState(false);

  const value = useMemo(() => {
    const subtotal = state.items.reduce((s, i) => s + i.price * i.quantity, 0);
    const itemCount = state.items.reduce((s, i) => s + i.quantity, 0);
    const shippingFee = subtotal > 0 && subtotal < 500 ? 49 : 0;
    const total = subtotal + shippingFee;

    return {
      items: state.items,
      subtotal,
      shippingFee,
      total,
      itemCount,
      open,
      openCart: () => setOpen(true),
      closeCart: () => setOpen(false),
      addItem: (product) => {
        dispatch({ type: 'ADD', product });
        setOpen(true);
      },
      incItem: (id) => dispatch({ type: 'INC', id }),
      decItem: (id) => dispatch({ type: 'DEC', id }),
      removeItem: (id) => dispatch({ type: 'REMOVE', id }),
      clearCart: () => dispatch({ type: 'CLEAR' }),
    };
  }, [state, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
